import { useMemo } from "react";
import { buildChartTheme } from "@visx/xychart";
import type { XYChartTheme } from "@visx/xychart";
import { useChartColors } from "./colors";

/**
 * The XYChart theme built from our design tokens. Fed to `<XYChart theme>` (see
 * {@link XYChart}) so series color, grid, axes, and labels are on-brand and
 * dark-mode-correct with **zero author color props** — visx's own default axis
 * text is hardcoded `#222`, so injecting this is exactly what turns an unstyled
 * visx chart into a themed one. Series are colored from `colors` (the ordinal
 * ramp) keyed by each series' `dataKey`.
 */
export function useChartTheme(): XYChartTheme {
  const c = useChartColors();
  return useMemo(
    () =>
      buildChartTheme({
        backgroundColor: c.background,
        colors: c.ramp,
        tickLength: 4,
        gridColor: c.border,
        gridColorDark: c.border,
        // The scaffold's <ChartTooltip> renders HTML — theme it to match
        // ChartTooltipCard's chrome so both tooltip paths read identically.
        htmlLabel: {
          color: c.foreground,
          background: c.card,
          border: `1px solid ${c.border}`,
          borderRadius: 8,
          padding: "4px 8px",
        },
        svgLabelSmall: { fill: c.mutedForeground, fontSize: 11 },
        svgLabelBig: { fill: c.foreground, fontSize: 12 },
        xAxisLineStyles: { stroke: c.border },
        yAxisLineStyles: { stroke: c.border },
        xTickLineStyles: { stroke: c.border },
        yTickLineStyles: { stroke: c.border },
      }),
    [c],
  );
}
